import { Component, OnInit } from '@angular/core';
import { Organisation } from '../../modules/organisation/lib/model/Organisation';
import { OrganisationService } from '../../modules/organisation/lib/services/organisation.service';
import { AlertController, ModalController, NavController } from '@ionic/angular/standalone';
import { IonHeader, IonToolbar, IonTitle, IonContent, IonList, IonItem, IonLabel, IonText } from '@ionic/angular/standalone';
import { UpdateOrganisationDto } from '../../modules/organisation/lib/dto/update-organisation.dto';
import { AccountType, ApiModules, ApiOperations, DisplayService, ValidationUtil } from '@qid/core';
import { Store } from '@ngrx/store';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { OrganisationDetails } from '../../modules/organisation/lib/model/OrganisationDetails';
import { activeAccountChanges, AuthActions, AuthSelectors } from '../../modules/authentication';
import { Router } from '@angular/router';
import { SelectIndustryComponent } from '../../modules/industry';
import { OrganisationUserActions, PermissionDirective, OrganisationUserSelectors } from "../../modules/organisation-user";
import { FormsModule } from '@angular/forms';
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent, SuperInputComponent, AvatarRoundComponent } from '@qid/super';
import { AddressFillService } from '../../services/address-fill.service';

@Component({
  selector: 'app-edit-organisation',
  templateUrl: './edit-organisation.page.html',
  styleUrls: ['./edit-organisation.page.scss'],
  standalone: true,
  imports: [
    FormsModule,
    SexyModule,
    SuperButtonComponent,
    SuperInputComponent,
    AvatarRoundComponent,
    PermissionDirective,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonList,
    IonItem,
    IonLabel,
    IonText
  ],
})
export class EditOrganisationPage implements OnInit {

  org: Organisation = null;
  canEdit = false;
  updateOrgDto: UpdateOrganisationDto = new UpdateOrganisationDto();
  private orgSnapshot = '';

  // Validation states
  pincodeValidationTimeout: any = null;

  // Field-level validation errors
  validationErrors: {
    city?: string;
    state?: string;
    pinCode?: string;
  } = {};

  constructor(
    private organisationService: OrganisationService,
    private navCtrls: NavController,
    private router: Router,
    private alertController: AlertController,
    private displayService: DisplayService,
    private store: Store,
    private modalController: ModalController,
    private addressFillService: AddressFillService
  ) {
  }

  private leave$ = new Subject<void>();

  ngOnInit() {
  }

  ionViewDidEnter() {
    this.leave$ = new Subject<void>();
    activeAccountChanges(this.store)
      .pipe(takeUntil(this.leave$))
      .subscribe(() => this.setActiveOrg());
  }

  ionViewDidLeave() {
    this.leave$.next();
    this.leave$.complete();
  }

  async setActiveOrg() {
    const [activeOrg, activeOrgUser] = await Promise.all([
      firstValueFrom(this.store.select(AuthSelectors.selectActiveAccount)),
      firstValueFrom(this.store.select(OrganisationUserSelectors.selectActiveOrganisationUser)),
    ]);
    if (activeOrg) {
      this.org = JSON.parse(JSON.stringify(activeOrg));
      if (!this.org.organisationDetails) {
        this.org.organisationDetails = new OrganisationDetails();
      }
    }
    this.canEdit = PermissionDirective.hasPermission(
      activeOrgUser,
      ApiModules.Organisation,
      ApiOperations.update
    );
    this.orgSnapshot = this.buildSnapshot();
  }

  get hasUnsavedChanges(): boolean {
    return !!this.org && this.orgSnapshot !== this.buildSnapshot();
  }

  private buildSnapshot(): string {
    if (!this.org?.organisationDetails) {
      return '';
    }
    const d = this.org.organisationDetails;
    return JSON.stringify({
      name: this.org.name,
      phoneNumber: d.phoneNumber,
      country: d.country,
      pinCode: d.pinCode,
      city: d.city,
      state: d.state,
      addressLine: d.addressLine,
      pan: d.pan,
      gst: d.gst,
    });
  }

  deleteOrg = async () => {

    const res = await this.organisationService.delete(this.org._id);


    if (!res || res.errorMessage) {
      await this.displayService.toast({ message: res.errorMessage });
      return;
    }

    if (res && res.data?.deleteTime) {
      await this.displayService.toast({ message: 'Organisation Deleted' });
      this.store.dispatch(OrganisationUserActions.switchUserOrganisation({ organisation: null, persist: true }))
      this.store.dispatch(OrganisationUserActions.fetchUserOrganisations())
      this.navCtrls.back()
    }

  };

  alertDelete = async () => {
    const alert = await this.alertController.create({
      header: `Delete ${this.org.name} Permanently?`,
      subHeader:
        'Once confirmed, all the Collected IDs, Templates, Contacts and Other Business data will be deleted permanently',
      buttons: [
        {
          text: 'Cancel',
          role: 'cancel'
        },
        {
          text: 'Delete',
          role: 'destructive',
          handler: () => {
            this.deleteOrg();
          }
        }
      ]
    });
    await alert.present();
  };

  public async editIndustry() {
    if (!this.canEdit) {
      return;
    }
    const modal = await this.modalController.create({
      component: SelectIndustryComponent,
      keyboardClose: false
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (!data.industryId) {
      return;
    }
    this.updateOrgDto.industryId = data.industryId;
    await this.updateOrg();

  }

  uploadOrgDisplayPic = async (file: any) => {

    const activeOrg = await firstValueFrom(this.store.select(AuthSelectors.selectActiveAccount));

    if (activeOrg != null) {
      const response = await this.organisationService.updateDisplayPic(
        file.file,
        activeOrg._id
      );

      if (!response || !response.data) {
        return;
      }

      this.store.dispatch(AuthActions.setActiveAccount({ account: response.data, accountType: AccountType.organisation }))

      this.setActiveOrg();

    }

  };


  /**
   * Validates pincode and auto-fills city and state
   * Triggered when pincode changes
   */
  async onPincodeChange() {
    if (!this.canEdit) return;

    const pincode = this.org.organisationDetails.pinCode?.trim();
    const country = this.org.organisationDetails.country;

    // Clear previous validation
    delete this.validationErrors.pinCode;
    delete this.validationErrors.city;
    delete this.validationErrors.state;

    // Debounce: clear existing timeout
    if (this.pincodeValidationTimeout) {
      clearTimeout(this.pincodeValidationTimeout);
    }

    // Require at least 3 characters before validating
    if (!pincode || pincode.length < 3) {
      this.org.organisationDetails.city = '';
      this.org.organisationDetails.state = '';
      return;
    }

    // Debounce validation by 800ms
    this.pincodeValidationTimeout = setTimeout(async () => {
      await this.validatePincodeAndFetchAddress(pincode, country);
    }, 800);
  }

  /**
   * Performs the actual validation and address fetch
   */
  private async validatePincodeAndFetchAddress(pincode: string, country: string) {
    try {
      if (!country || country.trim() === '') {
        this.validationErrors.pinCode = 'Please select a country first';
        return;
      }

      // Call validation service with full country name
      const result = await this.addressFillService.validatePostalCode(pincode, country);

      if (result.isValid) {
        // Auto-fill city and state
        this.org.organisationDetails.city = result.city;
        this.org.organisationDetails.state = result.state;
      } else {
        this.validationErrors.pinCode = result.message;
        
        // Clear city and state on invalid pincode
        this.org.organisationDetails.city = '';
        this.org.organisationDetails.state = '';
      }
    } catch (error) {
      console.error('Pincode validation error:', error);
      // Don't show error to user, just log it
    }
  }

  /**
   * Validates city field
   */
  validateCity() {
    if (!this.canEdit) return;
    
    const city = this.org.organisationDetails.city;
    
    if (!city || city.trim() === '') {
      this.validationErrors.city = 'City is required';
    } else if (city.trim().length < 2) {
      this.validationErrors.city = 'City name too short';
    } else if (city.trim().length > 100) {
      this.validationErrors.city = 'City name too long';
    } else {
      delete this.validationErrors.city;
    }
  }

  /**
   * Validates state field
   */
  validateState() {
    if (!this.canEdit) return;
    
    const state = this.org.organisationDetails.state;
    
    if (!state || state.trim() === '') {
      this.validationErrors.state = 'State is required';
    } else if (state.trim().length < 2) {
      this.validationErrors.state = 'State name too short';
    } else if (state.trim().length > 100) {
      this.validationErrors.state = 'State name too long';
    } else {
      delete this.validationErrors.state;
    }
  }

  /**
   * Validates pincode field
   */
  validatePincode() {
    if (!this.canEdit) return;
    
    const pincode = this.org.organisationDetails.pinCode;
    
    if (!pincode || pincode.trim() === '') {
      this.validationErrors.pinCode = 'Pincode is required';
    } else if (pincode.trim().length < 3) {
      this.validationErrors.pinCode = 'Pincode too short';
    } else if (pincode.trim().length > 10) {
      this.validationErrors.pinCode = 'Pincode too long';
    } else {
      delete this.validationErrors.pinCode;
    }
  }

  /**
   * Check if form has any validation errors
   */
  hasValidationErrors(): boolean {
    return Object.keys(this.validationErrors).length > 0;
  }


  public updateOrg = async () => {
    if (ValidationUtil.isStringEmpty(this.org.name)) {
      return this.displayService.toast({ message: 'Please enter Business Name.', color: 'warning' })
    }

    // Validate all fields before saving
    this.validateCity();
    this.validateState();
    this.validatePincode();

    // Check if there are any validation errors
    if (this.hasValidationErrors()) {
      const errorMessages = Object.values(this.validationErrors).join('. ');
      return this.displayService.toast({ 
        message: `Please fix validation errors: ${errorMessages}`, 
        color: 'danger',
        duration: 4000
      });
    }

    this.updateOrgDto.name = this.org.name;
    this.updateOrgDto.organisationDetails = this.org.organisationDetails;

    const updatedOrg = await this.organisationService.update(
      this.org._id,
      this.updateOrgDto
    );
    if (updatedOrg && updatedOrg._id) {
      this.store.dispatch(AuthActions.setActiveAccount({ account: updatedOrg, accountType: AccountType.organisation }))
      await this.setActiveOrg();
      // Clear validation errors when successfully saved
      this.validationErrors = {};
    }
  };

  protected readonly ApiOperations = ApiOperations;
  protected readonly ApiModules = ApiModules;
}
